"use client";

import { Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "What materials do you specialize in?",
  answer: "We specialize in high-pressure laminates, phenolic resins, and advanced composite materials. Our expertise lies in sourcing industrial-grade substrates that offer superior durability, chemical resistance, and structural integrity for demanding commercial environments."
}, {
  question: "How do you ensure material consistency?",
  answer: "Quality control is at the heart of our process. Every batch undergoes rigorous testing for density, surface finish, and thermal stability. We partner exclusively with certified manufacturers to ensure that every sheet meets international standards for performance and safety."
}, {
  question: "Can you provide custom fabrication services?",
  answer: "Absolutely. Beyond material supply, we offer precision CNC machining, edge banding, and custom cutting services. Our technical team works from your CAD files to deliver components ready for immediate installation, reducing your on-site labor costs."
}, {
  question: "What are your typical lead times?",
  answer: "Standard stock items are typically dispatched within 48 hours. For custom-fabricated orders, lead times generally range from 2 to 4 weeks depending on project complexity. We provide real-time tracking updates throughout the production cycle."
}, {
  question: "Do you offer bulk project pricing?",
  answer: "Yes, we provide tiered pricing structures for large-scale commercial and industrial projects. Our procurement specialists will analyze your bill of materials to offer the most competitive rates without compromising on material quality."
}, {
  question: "What makes your phenolic panels unique?",
  answer: "Our phenolic panels are engineered for extreme environments. They feature a non-porous, anti-bacterial surface that is impervious to moisture, impact, and harsh cleaning agents, making them the gold standard for laboratories and public facilities."
}, {
  question: "Is technical support available for installation?",
  answer: "We provide comprehensive technical documentation, including installation guides, hardware recommendations, and maintenance protocols. Our engineering team is also available for remote consultations to assist with complex assembly requirements."
}, {
  question: "How do I request a material sample?",
  answer: "You can request a sample kit through our contact portal. We maintain a library of our most popular finishes and textures, which can be shipped to your office or job site within a few business days for your evaluation."
}];
export default function Faq() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2463" id="faq" className="bg-background text-foreground py-24 md:py-32 lg:py-40 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="lg:sticky lg:top-24">
              <div className="font-mono text-xs tracking-widest uppercase text-primary mb-8">
                TECHNICAL SUPPORT / 03
              </div>

              <h2 className="font-default font-bold text-foreground mb-10" style={{
              fontSize: "clamp(2.75rem, 5vw, 5rem)",
              lineHeight: "0.95",
              letterSpacing: "-0.03em"
            }}>
                Expert
                <br />
                Answers
                <br />
                Provided.
              </h2>

              <p className="font-default text-base md:text-lg leading-relaxed mb-10" style={{
              maxWidth: "360px",
              color: "var(--card-foreground)"
            }}>
                We are here to support your project from specification to installation. If you have specific technical requirements, our team is ready to assist.
              </p>

              <Button variant="outline" size="lg" className="font-default font-medium tracking-wider uppercase text-xs border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 px-8 py-6 h-auto">
                Contact Our Team
              </Button>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Accordion type="single" collapsible defaultValue="item-0" className="flex flex-col gap-3">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-0 backdrop-blur-sm transition-all duration-300 group" style={{
              backgroundColor: "var(--card)",
              border: "1px solid var(--border)",
              boxShadow: "var(--shadow-md)"
            }}>
                  <AccordionTrigger className="hover:no-underline px-6 md:px-8 py-6 md:py-7 min-h-[96px] gap-6 [&>svg]:hidden group-hover:[border-color:var(--primary)]">
                    <div className="flex items-center gap-6 md:gap-8 flex-1 text-left">
                      <span className="font-default font-bold text-xs text-primary shrink-0 tracking-wider">
                        Q.{String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-default font-medium text-base md:text-lg text-foreground leading-snug">
                        {faq.question}
                      </span>
                    </div>
                    <Plus className="size-6 text-primary shrink-0 transition-transform duration-300 group-data-[state=open]:rotate-45" strokeWidth={1.5} />
                  </AccordionTrigger>
                  <AccordionContent className="px-6 md:px-8 pb-8 pt-0">
                    <div className="pl-0 md:pl-[88px]">
                      <p className="font-default text-base md:text-[1.0625rem]" style={{
                    lineHeight: "1.75",
                    maxWidth: "600px",
                    color: "var(--card-foreground)"
                  }}>
                        {faq.answer}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}